import React, { useState } from 'react';
import { ChevronDown, Sparkles, Check } from 'lucide-react';
import { PrimeIndemnite } from '../types';
import { calculateAdjustedPrimeAmount } from '../utils/primeCalculations';
import { formatCurrency } from '../utils/formatting';

interface PrimesCategoryAccordionProps {
  title: string;
  primes: PrimeIndemnite[];
  workTimePercentage: number;
  defaultOpen?: boolean;
  isRecommended?: boolean;
  isActiveGroup?: boolean;
  children: React.ReactNode;
}

export default function PrimesCategoryAccordion({
  title,
  primes,
  workTimePercentage,
  defaultOpen = false,
  isRecommended = false,
  isActiveGroup = false,
  children,
}: PrimesCategoryAccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const activeCount = primes.filter(p => p.active).length;
  const totalAmount = primes
    .filter(p => p.active)
    .reduce((sum, p) => sum + calculateAdjustedPrimeAmount(p, workTimePercentage), 0);

  const headerBorder = isRecommended
    ? 'border-green-300 bg-green-50'
    : isActiveGroup
      ? 'border-emerald-300 bg-emerald-50'
      : 'border-gray-200 bg-white';

  return (
    <div className={`rounded-lg border ${headerBorder} overflow-hidden mb-3`}>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/60 transition-colors"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isRecommended && <Sparkles className="w-4 h-4 text-green-600 flex-shrink-0" />}
          {isActiveGroup && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
          <span className="font-semibold text-gray-800 text-sm truncate">{title}</span>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {activeCount > 0 ? `${activeCount}/${primes.length} active${activeCount > 1 ? 's' : ''}` : `${primes.length} prime${primes.length > 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {totalAmount > 0 && (
            <span className="text-sm font-semibold text-green-700">
              {formatCurrency(totalAmount)}
            </span>
          )}
          <ChevronDown
            className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="px-3 pb-3 pt-1 bg-white">
          {children}
        </div>
      )}
    </div>
  );
}
